// ─────────────────────────────────────────────────────────────
// SINGLE SOURCE OF TRUTH FOR ALL SITE CONTENT.
// Items marked PLACEHOLDER are waiting on Arush's real details.
// Case studies: REAL executed campaigns from user-supplied one-pagers.
// ─────────────────────────────────────────────────────────────

export const site = {
  name: "ARUSH TANDON",
  role: "Brand & Experience Strategist",
  company: "Associate Manager — Strategy & Planning · Impact Communications",
  location: "India",
  email: "arushtandon85@gmail.com",
  phone: "+91 98186 11978",
  phoneLink: "tel:+919818611978",
  linkedin: "https://www.linkedin.com/in/arush-tandon-4a2bb51a8/",
  linkedinLabel: "linkedin.com/in/arush-tandon",
  resumeUrl: "/arush-tandon-resume.pdf",
};

export const heroLines = ["STRATEGY THAT", "GETS OFF", "THE SLIDE."];

export const heroSupport =
  "I turn consumer insight, culture and business problems into brand ideas people can experience, participate in and act on.";

export const heroDisciplines =
  "Brand Strategy · Creative Strategy · Experiential · Rural & Emerging Markets · Integrated Campaigns";

export const marqueeBrands = [
  "HALEON",
  "SENSODYNE",
  "ENO",
  "CENTRUM",
  "MONDELEZ",
  "BOURNVITA",
  "ITC",
  "AASHIRVAAD",
  "DARK FANTASY",
  "SUNFEAST MARIE LIGHT",
  "BAJAJ",
  "UJJIVAN",
  "APOLLO TYRES",
  "EVEREADY",
  "SPINNER",
];

export const positioningHeadline = [
  "FROM THE BUSINESS PROBLEM",
  "TO THE HUMAN PROBLEM.",
];

export const positioningCopy = [
  "The most useful strategy usually begins by reframing the brief.",
  "A trial problem might actually be a trust problem. A product-launch problem might be a retailer-confidence problem. A rural-reach problem might be a relationship problem. And sometimes a brand does not need another activation — it needs a platform people can continue participating in after the activation disappears.",
  "My work sits in that space between the brief and the behaviour.",
];

export const stages = [
  {
    n: "01",
    title: "FIND THE TENSION",
    text: "Consumer, category, culture and context.",
  },
  {
    n: "02",
    title: "FRAME THE STRATEGY",
    text: "Turn observations into a clear behavioural opportunity.",
  },
  {
    n: "03",
    title: "BUILD THE IDEA",
    text: "Create a platform rather than a collection of disconnected tactics.",
  },
  {
    n: "04",
    title: "DESIGN FOR SCALE",
    text: "Connect communication, experience, retail, digital and measurement.",
  },
];

export const disciplines = [
  "Brand Strategy",
  "Creative Strategy",
  "Consumer & Cultural Insights",
  "Experiential Marketing",
  "Integrated Campaign Planning",
  "Rural & Emerging Market Strategy",
  "BTL & Brand Activations",
  "Go-To-Market Strategy",
  "Trade & Retail Marketing",
  "Campaign Architecture",
  "Storytelling & Presentation Development",
];

// Case studies 1–5: REAL executed campaigns (from supplied one-pagers).
// Case study 6: DRAFT — replace with real Apollo Tyres / next project details.
export const caseStudies = [
  {
    slug: "haleon-health-mission",
    brand: "HALEON — SENSODYNE · ENO · CENTRUM · VOLTAREN",
    project: "Health Mission",
    thought:
      "Healthcare doesn't begin when people visit a clinic. It begins when trusted advice reaches people where they live.",
    status: "EXECUTED CAMPAIGN",
    tags: ["Rural & Emerging Markets", "Experiential Marketing", "BTL & Brand Activations"],
    year: null,
    role: "Strategy & Planning",
    image: "/images/haleon-health-mission.webp",
    sections: [
      {
        heading: "The Brief",
        body: [
          "Millions in rural India lacked easy access to trusted healthcare guidance — everyday health concerns never translated into informed product adoption or preventive care.",
          "The task: take Haleon's everyday-health portfolio beyond the clinic and make it relevant where healthcare actually happens — at the doorstep.",
        ],
      },
      {
        heading: "The Human Truth",
        body: [
          "Healthcare doesn't begin when people visit a clinic. It begins when trusted advice reaches people where they live.",
          "In these communities, a health claim is only as strong as the person delivering it — so the brand had to show up as a trusted advisor, not an advertisement.",
        ],
      },
      {
        heading: "The Platform",
        body: [
          "The Health Mission — a travelling healthcare platform built from Health Ambassadors, Mobile Health Stations and innovative demonstration tools that educated communities, encouraged product trials and brought trusted healthcare directly to rural India.",
          "Sensodyne Chill Tests, ENO demonstrations, Centrum sampling and oral-health education turned awareness into participation and first-hand product experience.",
        ],
      },
      {
        heading: "Designed for Scale",
        body: [
          "Bike Utility Bags, Health Ambassador belts and door-to-door outreach extended the experience beyond the activation site into every household.",
          "QR-enabled reporting created a scalable, measurable model — a consistent healthcare experience delivered across 1,500 villages.",
        ],
      },
    ],
    metrics: [
      { value: "1,500", label: "Villages covered" },
      { value: "130", label: "Health Ambassadors" },
      { value: "1,80,000+", label: "Consumers engaged" },
      { value: "1,00,000+", label: "Chill Tests conducted" },
      { value: "₹35 LAKH+", label: "Sales generated" },
    ],
    activities: [
      "Mobile Health Stations — immersive healthcare experience zones across villages and marketplaces",
      "Health Ambassador-led Sensodyne Chill Tests, ENO demos, Centrum sampling and oral-health education",
      "Bike Utility Bags, Ambassador belts and door-to-door outreach beyond the activation site",
      "QR-enabled reporting for a measurable, repeatable village model",
    ],
    outcome:
      "Haleon transformed rural health education into a scalable behaviour-change platform — combining innovation, product trial and community engagement to make trusted everyday healthcare accessible where it was needed most.",
  },
  {
    slug: "dark-fantasy-big-fantasies",
    brand: "ITC — DARK FANTASY",
    project: "Big Fantasies",
    thought:
      "Schools are bound by routine and academic pressure — so we built a travelling experience that turned an ordinary school day into an extraordinary one.",
    status: "EXECUTED CAMPAIGN",
    tags: ["Creative Strategy", "Experiential Marketing", "Consumer & Cultural Insights"],
    year: null,
    role: "Strategy & Planning",
    image: "/images/dark-fantasy-big-fantasies.webp",
    sections: [
      {
        heading: "The Brief",
        body: [
          "Dark Fantasy — 'Har Dil Ki Fantasy' — wanted to expand its reach by engaging children aged 4–14 for the first time, transforming ordinary moments into extraordinary ones through a memorable, interactive campaign.",
          "The bar was high: the campaign had to meet the creative benchmark set by the brand's previous Shah Rukh Khan campaign.",
        ],
      },
      {
        heading: "The Human Truth",
        body: [
          "Schools are bound by routine, and kids often face academic pressure. The most extraordinary thing you can give a child on an ordinary school day is permission to imagine.",
        ],
      },
      {
        heading: "The Platform",
        body: [
          "Big Fantasies — 'Give wings to your imagination.' A travelling, interactive experience where children's own drawings came alive around them, blending art, science and brand storytelling inside a space-themed world.",
          "Kids didn't watch the brand's fantasy — they walked inside their own.",
        ],
      },
      {
        heading: "Designed for Scale",
        body: [
          "A branded bus-turned-experience travelled school to school across four cities, with drawing-led participation, take-home artworks and shareable moments designed into every stop.",
        ],
      },
    ],
    metrics: [
      { value: "1 LAKH+", label: "Children engaged" },
      { value: "500", label: "Schools activated" },
      { value: "4", label: "Major cities" },
      { value: "2-DIGIT", label: "Sales growth in activated markets" },
    ],
    activities: [
      "Interactive bus experience bringing children's drawings to life",
      "School-by-school activation format across 4 cities",
      "Take-home artworks extending the experience to families",
      "Blended art, science and brand storytelling — praised by children as 'magical'",
    ],
    outcome:
      "Recognised as a benchmark in blending art, science and brand storytelling — and praised by its toughest critics, the children themselves, as 'magical'.",
  },
  {
    slug: "aashirvaad-andheri-cha-raja",
    brand: "ITC — AASHIRVAAD ATTA",
    project: "Every Grain Resides Lord Ganesha",
    thought:
      "Only 9% of Mumbai households trust packaged atta — so instead of arguing purity, we put Lord Ganesha on a single grain of wheat.",
    status: "EXECUTED CAMPAIGN",
    tags: ["Brand Strategy", "Experiential Marketing", "Integrated Campaign Planning"],
    year: null,
    role: "Strategy & Planning",
    image: "/images/aashirvaad-andheri-cha-raja.webp",
    sections: [
      {
        heading: "The Brief",
        body: [
          "Reinforce that Aashirvaad Atta is crafted from the purest wheat and connect with Mumbai audiences during Ganesh Chaturthi — a festival where purity in food reflects purity in devotion.",
        ],
      },
      {
        heading: "The Human Truth",
        body: [
          "Only 9% of households in Mumbai use packaged atta — most still trust loose wheat for purity. You can't out-argue that belief; you have to out-symbolise it.",
          "Ganesh Chaturthi celebrates purity, devotion and sacred food — the perfect platform for the brand's truth.",
        ],
      },
      {
        heading: "The Platform",
        body: [
          "'गहू-गहू आहे खास, प्रत्येक गहूत गणपतीचा वास' — every grain is special, for in every grain resides Lord Ganesha. At Andheri Cha Raja, devotees encountered Lord Ganesha on a single grain of wheat: personalised digital projections on a giant grain, and the smallest Ganesh idols hand-painted on real wheat grains by a live artist.",
        ],
      },
      {
        heading: "Designed for Scale",
        body: [
          "Devotional giveaways — modak prasad, aarti booklets and atta packs — carried the experience home, while influencers and 65+ media outlets amplified it. The distribution of 2,005 grain-idols earned India & Asia Book of Records recognition.",
        ],
      },
    ],
    metrics: [
      { value: "87K+", label: "Visitors reached at Andheri Cha Raja" },
      { value: "20K+", label: "Engaged with immersive activities" },
      { value: "5K+", label: "Artworks created & projected" },
      { value: "4K+", label: "Keepsakes shared on WhatsApp" },
      { value: "2,005", label: "Grains distributed — India & Asia Book of Records" },
    ],
    activities: [
      "Digital Ganesha projection — personalised artworks on a giant wheat grain, shared via WhatsApp keepsakes",
      "Live artist spectacle — the smallest Ganesh idols painted on wheat grains",
      "World Record — biggest distribution of Ganesh idols on wheat grains",
      "Devotional giveaways — modak prasad, aarti booklets and atta packs taken home",
      "Amplification through influencers and 65+ media outlets",
    ],
    outcome:
      "A purity claim became a devotional experience — earning a world record, city-wide conversation and a place in the India & Asia Book of Records.",
  },
  {
    slug: "bajaj-heater-zone",
    brand: "BAJAJ",
    project: "The Heater Zone @ Magh Mela",
    thought:
      "In the harsh winter of Magh Mela, warmth is the first need pilgrims seek — so the brand became the warmest place at the mela.",
    status: "EXECUTED CAMPAIGN",
    tags: ["Experiential Marketing", "Rural & Emerging Markets", "Trade & Retail Marketing"],
    year: "2026",
    role: "Strategy & Planning",
    image: "/images/bajaj-heater-zone-magh-mela.jpeg",
    sections: [
      {
        heading: "The Brief",
        body: [
          "Use Bajaj Heaters as the primary winter need-state hook at Magh Mela 2026 — while seamlessly showcasing Bajaj's complete home-appliance range.",
        ],
      },
      {
        heading: "The Human Truth",
        body: [
          "In the harsh winter of Magh Mela, warmth is the first need pilgrims seek. A brand that provides real comfort in this moment of faith earns trust, attention — and openness to discover more.",
        ],
      },
      {
        heading: "The Platform",
        body: [
          "The Bajaj Heater Zone — a giant heater-shaped stall designed as a beacon of warmth. Bajaj Heaters drew pilgrims in; inside, Bajaj's entire appliance ecosystem revealed itself.",
          "The heater became the symbol; the full range became the solution.",
        ],
      },
      {
        heading: "Designed for Scale",
        body: [
          "The warmth zone anchored a full participation system — live demonstrations, puppet-show storytelling, AI photo-ops at the ghats, balloon giveaways, and lead generation wired into retail conversion.",
        ],
      },
    ],
    metrics: [
      { value: "47,000", label: "Total footfall" },
      { value: "28,300", label: "Consumer approaches" },
      { value: "9,257", label: "Total engagements" },
      { value: "3,087", label: "Demo participations" },
      { value: "1,727", label: "AI photo engagements" },
    ],
    activities: [
      "Heater-shaped Warmth Zone — the hero entry and mela landmark",
      "Complete Bajaj range showcase inside the experience",
      "Live demonstrations and product education",
      "Puppet-show storytelling and AI photo-ops at the ghats",
      "Lead generation wired into retail conversion",
    ],
    outcome:
      "A need-state became a landmark — 47,000 pilgrims walked toward the warmth, and left having met the entire Bajaj range.",
  },
  {
    slug: "ujjivan-vighnache-visarjan",
    brand: "UJJIVAN SMALL FINANCE BANK",
    project: "Vighnache Visarjan",
    thought:
      "Today's real vighnas are financial burdens — so Ujjivan stepped in as the modern-day remover of financial worries.",
    status: "EXECUTED CAMPAIGN",
    tags: ["Rural & Emerging Market Strategy", "Experiential Marketing", "Brand Strategy"],
    year: null,
    role: "Strategy & Planning",
    image: "/images/ujjivan-vighnache-visarjan.webp",
    sections: [
      {
        heading: "The Brief",
        body: [
          "Create a deep cultural and emotional connect during Ganesh Chaturthi — positioning Ujjivan not just as a bank, but as a trusted partner in removing life's obstacles.",
        ],
      },
      {
        heading: "The Human Truth",
        body: [
          "Ganesh Chaturthi is when devotees surrender their worries to Bappa, the Vighnaharta — the remover of obstacles.",
          "Today, people's real vighnas are financial burdens. The emotion already existed; the brand simply had to stand inside it honestly.",
        ],
      },
      {
        heading: "The Platform",
        body: [
          "'Ujjivan Vighnache Visarjan' — a symbolic act where people could dissolve their real-life worries, with faith that both Bappa and Ujjivan stood by their side.",
          "Worries written on dissolvable slips were immersed in water — mirroring visarjan — as Lord Ganesha appeared on screen the moment each worry disappeared. A goosebump moment, by design.",
        ],
      },
      {
        heading: "Designed for Scale",
        body: [
          "The Ujjivan Van took the experience to RWAs and festive hubs; printed blessing cards carried the emotion home; an interactive AR filter with aarti sounds and divine visuals extended the ritual through QR codes and newspaper ads.",
        ],
      },
    ],
    metrics: [
      { value: "5,000+", label: "Consumers directly engaged" },
      { value: "1,100+", label: "Real-life worries dissolved" },
      { value: "1,000+", label: "Blessing cards carried home" },
      { value: "1.5L+", label: "Engagements across on-ground, AR & QR" },
      { value: "17L+", label: "Total reach incl. AR & print" },
    ],
    activities: [
      "Ujjivan Van across RWAs and festive hubs — the experience travelled to communities",
      "Dissolvable worry slips immersed in water, mirroring the visarjan ritual",
      "Lord Ganesha appearing on screen as each worry disappeared",
      "Printed blessing cards shared with every participant",
      "Interactive AR filter with aarti sounds, extended via QR codes and newspaper ads",
    ],
    outcome:
      "A bank became part of a sacred ritual — 1,100+ real worries dissolved, and the emotion travelled far beyond the activation through AR and print.",
  },
  {
    slug: "apollo-travelling-dealership",
    brand: "APOLLO TYRES",
    project: "The Travelling Dealership",
    thought:
      "Rural growth begins when access replaces distance. If customers can't reach the dealership, the dealership must reach them.",
    status: "EXECUTED CAMPAIGN",
    tags: ["Trade & Retail Marketing", "Rural & Emerging Markets", "BTL & Brand Activations"],
    year: null,
    role: "Strategy & Planning",
    image: "/images/apollo-travelling-dealership.webp",
    sections: [
      {
        heading: "The Brief",
        body: [
          "Thousands of rural customers lived beyond the practical reach of Apollo's Authorised Retail Dealers — limiting product access, dealer growth and commercial opportunities despite existing demand.",
        ],
      },
      {
        heading: "The Human Truth",
        body: [
          "Rural growth begins when access replaces distance. If customers can't reach the dealership, the dealership must reach them.",
        ],
      },
      {
        heading: "The Platform",
        body: [
          "Apollo transformed its dealership into the Rural Mobile Store — a travelling showroom, commerce platform and last-mile distribution model that brought products, expertise and dealer connectivity directly into underserved villages.",
          "Product demonstrations, expert guidance and on-the-spot commerce converted village visits into immediate business opportunities.",
        ],
      },
      {
        heading: "Designed for Scale",
        body: [
          "Mechanics, village leaders and prospective sub-dealers were engaged to build a sustainable rural distribution ecosystem — every activation generated sales, qualified leads and future dealer expansion, leaving behind a stronger market than before.",
        ],
      },
    ],
    metrics: [
      { value: "52", label: "Villages activated" },
      { value: "58%", label: "Sales uplift" },
      { value: "10+", label: "Sub-dealers identified" },
      { value: "60+", label: "Mechanics engaged" },
      { value: "20+", label: "Business opportunities" },
    ],
    activities: [
      "Mobile dealership covering villages beyond the existing ARD network",
      "Product demonstrations, expert guidance and on-the-spot commerce",
      "Mechanic, village-leader and sub-dealer engagement for a rural distribution ecosystem",
      "Sales, qualified leads and dealer expansion built into every activation",
    ],
    outcome:
      "Apollo transformed a travelling showroom into a scalable rural commerce and last-mile distribution model — proving that the most powerful dealership is the one that goes where customers need it most.",
  },
  {
    slug: "bournvita-nutrition-express",
    brand: "MONDELEZ — CADBURY BOURNVITA",
    project: "Nutrition Express",
    thought:
      "Children's diets were quietly falling short of what they needed — so we gave mothers a meter that made invisible nutrition visible.",
    status: "EXECUTED CAMPAIGN",
    tags: ["Integrated Campaign Planning", "BTL & Brand Activations", "Trade & Retail Marketing"],
    year: null,
    role: "Strategy & Planning",
    image: "/images/bournvita-nutrition-express.webp",
    sections: [
      {
        heading: "The Brief",
        body: [
          "Educate mothers about the importance of nutrition and promote Bournvita as a beneficial addition to their children's diet — maximising product awareness and availability.",
        ],
      },
      {
        heading: "The Human Truth",
        body: [
          "Despite living in urban areas with better access to resources, many children suffer nutritional inadequacies because their diets do not meet their developmental needs.",
          "Mothers don't lack care — they lack visibility into the gap. You can't fix what you can't see.",
        ],
      },
      {
        heading: "The Platform",
        body: [
          "The Nutrition Express — a direct-engagement campaign built around the Nutri-Check Meter, an innovative tool that helped mothers track their children's nutrient intake and see the nutritional improvement when Bournvita is included in the diet.",
          "The gap stopped being a claim and became a number on a screen — the mother's own number.",
        ],
      },
      {
        heading: "Designed for Scale",
        body: [
          "Door-to-door engagements across Amritsar, Delhi, Pune, Kanpur and Ludhiana demonstrated the meter mother to mother, while trade engagements secured availability and giveaways like pens and fridge magnets kept the brand in the kitchen.",
        ],
      },
    ],
    metrics: [
      { value: "24 LAKH+", label: "Households reached" },
      { value: "10 LAKH+", label: "Packs of Bournvita sold" },
      { value: "1.5X", label: "Market penetration vs national average" },
    ],
    activities: [
      "Nutri-Check Meter — an app that tracks a child's nutrient intake vs daily needs",
      "Door-to-door mother engagements across 5 cities",
      "Trade engagements to secure availability and integrated distribution",
      "Incentivisation through everyday giveaways — pens and fridge magnets",
    ],
    outcome:
      "Significant market penetration in activated markets — growing at 1.5 times the national average, with over 10 lakh packs sold.",
  },
  {
    slug: "sunfeast-surjalaya",
    brand: "ITC — SUNFEAST MARIE LIGHT",
    project: "Surjalaya @ Rath Yatra",
    thought:
      "When the brand stands for the sun and the festival worships it, the magic lies in meaningful integration.",
    status: "EXECUTED CAMPAIGN",
    tags: ["Experiential Marketing", "Consumer & Cultural Insights", "Integrated Campaign Planning"],
    year: null,
    role: "Strategy & Planning",
    image: "/images/sunfeast-surjalaya.webp",
    sections: [
      {
        heading: "The Brief",
        body: [
          "With the sun at the heart of its identity, Sunfeast Marie Light found a natural connect with Rath Yatra — where the sun is deeply revered. Odisha's spiritual relationship with the sun became the setting for a brand presence that wasn't just seen, but deeply felt — emotionally, culturally and spiritually.",
        ],
      },
      {
        heading: "The Human Truth",
        body: [
          "At Rath Yatra, devotion follows the sun's path. A brand that stands for the sun doesn't need to interrupt the festival — it needs to belong to it.",
        ],
      },
      {
        heading: "The Platform",
        body: [
          "Surjalaya — a radiant experiential space where every activation symbolised the sun's journey from dawn to dusk: the Pull the Rath challenge racing a virtual sunset, sun-activated blessing cards that revealed themselves only in direct sunlight, and sun-kissed photo zones.",
          "A sensory, spiritual and symbolic zone where brand purpose, sun worship and festive spirit converged.",
        ],
      },
      {
        heading: "Designed for Scale",
        body: [
          "Sampling turned into 'warm giveaways' — Sunfeast Marie Light packs gifted as tokens of light — so every one of the 3 lakh+ visitors left carrying the brand's meaning home.",
        ],
      },
    ],
    metrics: [
      { value: "3,00,000+", label: "Footfalls" },
      { value: "1,00,000+", label: "Engaged through activities" },
      { value: "18,000+", label: "Winners rewarded" },
      { value: "1,00,000+", label: "Product samples gifted" },
    ],
    activities: [
      "Pull the Rath Challenge — racing a virtual sunset with teamwork and urgency",
      "Sun-Activated Blessing Cards — blessings revealed only in direct sunlight",
      "Sun-Kissed Photo Zones — glow-drenched, shareable memories",
      "Sampling & Warm Giveaways — packs gifted as tokens of light",
    ],
    outcome:
      "A biscuit brand became part of Odisha's most sacred procession — felt, not just seen, by over 3 lakh devotees.",
  },
  {
    slug: "eveready-ultima-launch",
    brand: "EVEREADY",
    project: "Ultima — The Counter-First Launch",
    thought:
      "A product-launch problem is often a retailer-confidence problem — so India's first portable vaporiser launched counter to counter, not screen to screen.",
    status: "EXECUTED CAMPAIGN",
    tags: ["Go-To-Market Strategy", "Trade & Retail Marketing", "BTL & Brand Activations"],
    year: "2026",
    role: "Strategy & Planning",
    image: "/images/eveready-ultima-launch.webp",
    sections: [
      {
        heading: "The Brief",
        body: [
          "Launch Eveready Ultima — India's 1st portable liquid vaporiser: a rechargeable mosquito-repellent machine that charges first, then works without being plugged in. Perfect for travel, bedrooms and homes with limited power sockets.",
          "Retailers across three states had to be reached, convinced and equipped — because a first-of-its-kind product needs a believer at the counter.",
        ],
      },
      {
        heading: "The Human Truth",
        body: [
          "In general trade, the retailer's word is the last and most persuasive medium. A retailer who believes in the product sells it; one who is unsure lets it sit.",
          "Retailer feedback said it best: 'Very compact, affordable and convenient in closed spaces such as cars — without worrying about continuous electricity supply.'",
        ],
      },
      {
        heading: "The Platform",
        body: [
          "A van-led launch activation — the Ultima van and a giant inflatable mosquito arriving market by market, with live product demonstrations that made 'portable and rechargeable' something retailers could hold, not just hear.",
        ],
      },
      {
        heading: "Designed for Scale",
        body: [
          "Pan-India retailer engagement across 5 cities, 3 states, 24 markets and 24 mandays — relationships deepened through close interaction and gifts (pens, clipboards, notepads, brochures) that kept Eveready on the counter after the van moved on.",
        ],
      },
    ],
    metrics: [
      { value: "5", label: "Cities covered" },
      { value: "3", label: "States covered" },
      { value: "24", label: "Markets activated" },
      { value: "24", label: "Mandays on ground" },
      { value: "720", label: "Gifts distributed to retailers" },
    ],
    activities: [
      "Ultima launch van with a giant inflatable mosquito — unmissable market-level theatre",
      "On-counter product demonstrations of the portable, rechargeable format",
      "Retailer relationship program with branded gifts — pens, clipboards, notepads, brochures",
      "Market-by-market rollout across 3 states in 24 mandays",
    ],
    outcome:
      "Won Silver — Best Product Launch Event in Emerging Markets, and Bronze — Retail & Point-of-Sale Engagement Campaign at the Flame Awards Asia 2026 (RMAI).",
  },
  {
    slug: "ujjivan-sound-of-diwali",
    brand: "UJJIVAN SMALL FINANCE BANK",
    project: "Sound of Diwali",
    thought:
      "For many, Diwali is seen. For some, Diwali is heard. When we experience their world, empathy becomes action.",
    status: "EXECUTED CAMPAIGN",
    tags: ["Experiential Marketing", "Brand Strategy", "Consumer & Cultural Insights"],
    year: null,
    role: "Strategy & Planning",
    image: "/images/ujjivan-sound-of-diwali.webp",
    sections: [
      {
        heading: "The Brief",
        body: [
          "Differentiate Ujjivan in a cluttered festive season by creating a deeply human, accessibility-led Diwali experience — reinforcing that prosperity grows when every individual is included in the joy of the festival.",
        ],
      },
      {
        heading: "The Human Truth",
        body: [
          "For many, Diwali is seen. For some, Diwali is heard.",
          "When we experience their world, empathy becomes action.",
        ],
      },
      {
        heading: "The Platform",
        body: [
          "A sensory-led initiative where people first felt Diwali without sight — a VR experience that appeared immersive until participants realised the screen was blank. The reveal: 'This is how the visually impaired experience Diwali.'",
          "Then came the answer: Diwali shared through sound — heartfelt festive wishes recorded on audio-enabled diyas, designed to light up lives through sound, not flame.",
        ],
      },
      {
        heading: "Designed for Scale",
        body: [
          "The Sound Diya Zone captured wishes, sensor-activated thank-you screens acknowledged every contribution live, and a distribution drive carried 800 audio diyas across 7 cities to 800 visually impaired individuals.",
        ],
      },
    ],
    metrics: [
      { value: "800", label: "Audio diyas created & gifted" },
      { value: "7", label: "Cities reached" },
      { value: "2,000+", label: "Participants" },
      { value: "10,000+", label: "People reached" },
      { value: "4,000+", label: "Meaningful interactions" },
    ],
    activities: [
      "VR Empathy Experience — immersive until the blank-screen reveal",
      "Sound Diya Zone — wishes recorded on audio-enabled diyas",
      "Live acknowledgment — sensor pads activating instant thank-you screens",
      "Distribution drive — 800 audio diyas to 800 visually impaired individuals across 7 cities",
    ],
    outcome:
      "India Book of Records — 'Largest Distribution of Festive Audio Greeting Devices to Specially Abled Individuals.' Also won Gold (Cause Event) and Bronze (Inclusive Event) at the Eventex Awards.",
  },
];

export const recognitionIntro =
  "Selected awards and records earned by campaigns strategised and planned at Impact Communications.";

export const recognition = [
  {
    body: "ET Brand Equity — Brand Disruption Awards '26",
    campaign: "Sunfeast Dark Fantasy · Big Fantasies",
    note: "Three wins across categories for the travelling imagination platform.",
    wins: [
      { medal: "WIN", category: "Most Disruptive Brand Activation Campaign" },
      { medal: "WIN", category: "Most Disruptive Experiential Campaign" },
      { medal: "WIN", category: "Most Disruptive Technology-led Campaign" },
    ],
    image: "/images/dark-fantasy-brand-disruption-2026.jpeg",
    year: "2026",
  },
  {
    body: "Flame Awards Asia 2026 · RMAI",
    campaign: "Eveready Ultima — Van Activation",
    note: "A powerful launch, two Flame wins in emerging markets.",
    wins: [
      { medal: "SILVER", category: "Best Product Launch Event in Emerging Markets" },
      { medal: "BRONZE", category: "Retail & Point-of-Sale Engagement Campaign" },
    ],
    image: "/images/eveready-flame-awards-2026.jpeg",
    year: "2026",
  },
  {
    body: "Eventex Awards",
    campaign: "Ujjivan Small Finance Bank · Sound of Diwali",
    note: "Diwali reimagined through inclusion, emotion and experience.",
    wins: [
      { medal: "GOLD", category: "Cause Event" },
      { medal: "BRONZE", category: "Inclusive Event" },
    ],
    image: "/images/ujjivan-sound-of-diwali-eventex.jpeg",
    year: null,
  },
  {
    body: "India & Asia Book of Records",
    campaign: "ITC Aashirvaad · Every Grain Resides Lord Ganesha",
    note: "The biggest distribution of Ganesh idols painted on wheat grains — 2,005 grains at Andheri Cha Raja.",
    wins: [{ medal: "RECORD", category: "India & Asia Book of Records recognition" }],
    image: "/images/aashirvaad-andheri-cha-raja.webp",
    year: null,
  },
  {
    body: "Impact Communications",
    campaign: "Spinner Sports Drink · Launch Event",
    note: "Launch event for the sports-drink entrant — 'Life is a Sport. Spin it to win it.'",
    wins: [{ medal: "SILVER", category: "Best Product Launch" }],
    image: "/images/spinner-launch-silver.jpeg",
    year: null,
  },
];

export const aboutBio = [
  "Arush Tandon is a brand and experience strategist at Impact Communications, India, where he works as Associate Manager — Strategy & Planning.",
  "His work sits at the intersection of brand strategy, consumer and cultural insight, and experiential thinking — with a particular pull toward rural and emerging markets, where brands are built in courtyards, haats and melas rather than on feeds.",
  "He has shaped campaigns and platforms across categories — healthcare, FMCG, consumer durables, automotive and financial services — for brands including Haleon (Sensodyne, ENO, Centrum, Voltaren), Mondelez (Cadbury Bournvita), ITC (Aashirvaad, Dark Fantasy, Sunfeast Marie Light), Bajaj, Apollo Tyres and Ujjivan Small Finance Bank.",
  "The through-line: take the business problem, find the human truth inside it, and turn it into an idea people can experience, participate in and act on.",
];

export const closingCta = {
  headline: "THE BRIEF IS NEVER THE PROBLEM.",
  sub: "Bring me the business problem. I'll find the human one inside it.",
};
